<template>
  <aside
    id="primary-navigation"
    ref="sidebar"
    class="sidebar"
    :class="{ 'is-open': open, 'is-collapsed': collapsed }"
    :aria-label="$t('navigation.primary')"
  >
    <button
      ref="closeButton"
      type="button"
      class="sidebar-close"
      :aria-label="$t('button.close')"
      @click="$emit('close')"
    >
      <i class="bx bx-x" aria-hidden="true"></i>
    </button>
    <div class="logo mt-3">
      <img src="@/assets/images/logo-destinatarium-70.png" :alt="$t('common.logo_alt')" />
    </div>
    <h6 class="logo-text destinatarium">
      <span class="dest">Destinatarium</span>
    </h6>
    <div v-if="contextOptions.length > 1" class="context-switcher">
      <label for="active-context">{{ $t("navigation.context") }}</label>
      <select
        id="active-context"
        :value="activeContext"
        :disabled="switchingContext"
        @change="switchContext"
      >
        <option
          v-for="option in contextOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>
      <small v-if="contextError" role="alert">{{ contextError }}</small>
    </div>
    <nav>
      <ul v-if="showPlatformNavigation">
        <li>
          <router-link to="/admin" class="link" :title="collapsed ? $t('navigation.administration') : null">
            <i class="bx bx-dashboard me-1" aria-hidden="true"></i>
            <span class="sidebar-link-label">{{ $t("navigation.administration") }}</span>
          </router-link>
        </li>
        <li>
          <router-link to="/admin/accounts" class="link" :title="collapsed ? $t('navigation.accounts') : null">
            <i class="bx bx-buildings me-1"></i>
            <span class="sidebar-link-label">{{ $t("navigation.accounts") }}</span>
          </router-link>
        </li>
        <li v-if="canReadPlatformUsers">
          <router-link to="/admin/users" class="link" :title="collapsed ? $t('navigation.users') : null">
            <i class="bx bx-group me-1"></i>
            <span class="sidebar-link-label">{{ $t("navigation.users") }}</span>
          </router-link>
        </li>
        <li v-if="canReadPlatformDomains">
          <router-link to="/admin/domains" class="link" :title="collapsed ? $t('navigation.domains') : null">
            <i class="bx bx-globe me-1" aria-hidden="true"></i>
            <span class="sidebar-link-label">{{ $t("navigation.domains") }}</span>
          </router-link>
        </li>
        <li v-if="canReadPlatformEmails">
          <router-link to="/admin/emails" class="link" :title="collapsed ? $t('navigation.emails') : null">
            <i class="bx bx-envelope me-1" aria-hidden="true"></i>
            <span class="sidebar-link-label">{{ $t("navigation.emails") }}</span>
          </router-link>
        </li>
        <li v-if="canReadAudit">
          <router-link to="/admin/audit-events" class="link" :title="collapsed ? $t('navigation.audit') : null">
            <i class="bx bx-history me-1"></i>
            <span class="sidebar-link-label">{{ $t("navigation.audit") }}</span>
          </router-link>
        </li>
        <li v-if="canReadAudit">
          <router-link to="/admin/error-logs" class="link" :title="collapsed ? $t('navigation.error_logs') : null">
            <svg
              class="sidebar-menu-icon"
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <path d="M10.3 3.7 2.6 17a2 2 0 0 0 1.7 3h15.4a2 2 0 0 0 1.7-3L13.7 3.7a2 2 0 0 0-3.4 0Z" />
              <path d="M12 9v4" />
              <path d="M12 17h.01" />
            </svg>
            <span class="sidebar-link-label">{{ $t("navigation.error_logs") }}</span>
          </router-link>
        </li>
      </ul>
      <ul v-else>
        <li>
          <router-link to="/dashboard" class="link" :title="collapsed ? $t('navigation.home') : null">
            <i class="bx bx-cheese me-1"></i>
            <span class="sidebar-link-label">{{ $t("navigation.home") }}</span>
          </router-link>
        </li>
        <li>
          <router-link to="/domains" class="link" :title="collapsed ? $t('navigation.domains') : null">
            <i class="bx bx-globe me-1"></i>
            <span class="sidebar-link-label">{{ $t("navigation.domains") }}</span>
          </router-link>
        </li>
        <li>
          <router-link to="/emails" class="link" :title="collapsed ? $t('navigation.emails') : null">
            <i class="bx bx-envelope me-1"></i>
            <span class="sidebar-link-label">{{ $t("navigation.emails") }}</span>
          </router-link>
        </li>
        <li>
          <router-link to="/apikeys" class="link" :title="collapsed ? $t('navigation.api_keys') : null">
            <i class="bx bx-key me-1"></i>
            <span class="sidebar-link-label">{{ $t("navigation.api_keys") }}</span>
          </router-link>
        </li>
        <li>
          <router-link to="/plans" class="link" :title="collapsed ? $t('subscription.plans') : null">
            <i class="bx bx-credit-card-alt me-1"></i>
            <span class="sidebar-link-label">{{ $t("subscription.plans") }}</span>
          </router-link>
        </li>
        <li v-if="canReadMembers">
          <router-link to="/members" class="link" :title="collapsed ? $t('navigation.my_team') : null">
            <i class="bx bx-group me-1"></i>
            <span class="sidebar-link-label">{{ $t("navigation.my_team") }}</span>
          </router-link>
        </li>
      </ul>
    </nav>
    <div ref="accountMenuRoot" class="sidebar-footer">
      <button
        type="button"
        class="sidebar-collapse"
        :title="$t(collapsed ? 'navigation.expand_menu' : 'navigation.collapse_menu')"
        :aria-label="$t(collapsed ? 'navigation.expand_menu' : 'navigation.collapse_menu')"
        @click="$emit('toggle-collapsed')"
      >
        <i :class="['bx', collapsed ? 'bx-chevron-right' : 'bx-chevron-left']" aria-hidden="true"></i>
      </button>
      <div
        v-if="accountMenuOpen"
        id="sidebar-account-menu"
        class="sidebar-account-menu"
        role="menu"
      >
        <div class="sidebar-account-menu-header">
          <span class="sidebar-account-meta">
            <small class="sidebar-account-email">
              <span class="sidebar-account-email-dot" aria-hidden="true"></span>
              <span class="sidebar-account-email-text">{{ accountEmail }}</span>
            </small>
          </span>
        </div>
        <div class="sidebar-account-menu-links">
          <router-link to="/settings/profile" role="menuitem" @click="closeAccountMenu">
            <i class="bx bx-user" aria-hidden="true"></i>
            <span>{{ $t("navigation.my_profile") }}</span>
          </router-link>
          <router-link
            v-if="hasTeamContext"
            to="/settings/usage"
            role="menuitem"
            @click="closeAccountMenu"
          >
            <i class="bx bx-tachometer" aria-hidden="true"></i>
            <span>{{ $t("settings.usage") }}</span>
          </router-link>
          <router-link
            v-if="hasTeamContext"
            to="/settings/billing"
            role="menuitem"
            @click="closeAccountMenu"
          >
            <i class="bx bx-credit-card" aria-hidden="true"></i>
            <span>{{ $t("navigation.billing") }}</span>
          </router-link>
        </div>
        <button type="button" class="account-menu-logout" role="menuitem" @click="onLogout">
          <svg
            class="sidebar-logout-icon"
            viewBox="0 0 32 32"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M14 4H6.5A2.5 2.5 0 0 0 4 6.5v19A2.5 2.5 0 0 0 6.5 28H14" />
            <path d="M11 16h16" />
            <path d="m22 10 6 6-6 6" />
          </svg>
          <span>{{ $t('button.logout') }}</span>
        </button>
      </div>
      <button
        type="button"
        class="link sidebar-settings-link"
        aria-haspopup="menu"
        :aria-expanded="accountMenuOpen"
        aria-controls="sidebar-account-menu"
        :title="collapsed ? $t('navigation.my_account') : null"
        @click="toggleAccountMenu"
      >
        <span class="sidebar-account-avatar">
          <img
            v-if="avatarSrc && !avatarFailed"
            :src="avatarSrc"
            :alt="$t('navigation.avatar')"
            @error="avatarFailed = true"
          />
          <i v-else class="bx bx-user" aria-hidden="true"></i>
        </span>
        <span class="sidebar-account-meta">
          <strong>{{ displayName }}</strong>
          <small>{{ $t("navigation.my_account") }}</small>
        </span>
        <i
          :class="['bx', accountMenuOpen ? 'bx-chevron-down' : 'bx-chevron-up', 'sidebar-account-chevron']"
          aria-hidden="true"
        ></i>
      </button>
    </div>
  </aside>
</template>

<script>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import { useRouter } from "vue-router";
import { useUserStore } from "@/stores/user";
import { useAuthStore } from "@/stores/auth";
import { authService } from "@/services/authService";
import { API_URL } from "@/config/api";
import { useI18n } from "vue-i18n";

export default {
  name: "AppSidebar",
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    collapsed: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["close", "toggle-collapsed"],
  setup(props) {
    const router = useRouter();
    const userStore = useUserStore();
    const authStore = useAuthStore();
    const { t } = useI18n();
    const switchingContext = ref(false);
    const contextError = ref("");
    const closeButton = ref(null);
    const accountMenuRoot = ref(null);
    const accountMenuOpen = ref(false);
    const avatarFailed = ref(false);
    const user = computed(() => userStore.user || {});
    const displayName = computed(() => `${user.value.first_name || ""} ${user.value.last_name || ""}`.trim()
      || user.value.email
      || "");
    const accountEmail = computed(() => user.value.email || "");
    const avatarSrc = computed(() => {
      const value = user.value.avatar_url;
      return value ? new URL(value, API_URL).toString() : null;
    });
    const isPlatformAdmin = computed(() => userStore.isPlatformAdmin());
    const showPlatformNavigation = computed(() =>
      isPlatformAdmin.value && !userStore.hasActiveTeam(),
    );
    const activeContext = computed(() => userStore.user?.acct_team_uuid || "platform");
    const contextOptions = computed(() => {
      const options = isPlatformAdmin.value
        ? [{ value: "platform", label: t("navigation.platform_admin") }]
        : [];
      return options.concat((userStore.user?.teams || []).map((team) => ({
        value: team.uuid,
        label: team.short_name,
      })));
    });
    const canReadPlatformUsers = computed(() =>
      userStore.hasPermission("platform.users.read"),
    );
    const canReadPlatformEmails = computed(() =>
      userStore.hasPermission("platform.emails.read"),
    );
    const canReadPlatformDomains = computed(() =>
      userStore.hasPermission("platform.domains.read"),
    );
    const canReadMembers = computed(() =>
      userStore.hasTeamPermission("team.members.read"),
    );
    const hasTeamContext = computed(() => userStore.hasActiveTeam());
    const canReadAudit = computed(() => userStore.hasPermission("platform.audit.read"));
    const switchContext = async (event) => {
      const value = event.target.value;
      switchingContext.value = true;
      contextError.value = "";
      try {
        const user = await authService.selectTeam(value === "platform" ? null : value);
        userStore.setUser(user);
        await router.push(value === "platform" ? "/admin" : "/dashboard");
      } catch (error) {
        contextError.value = error.response?.data?.errorDescription
          || t("navigation.context_switch_error");
        event.target.value = activeContext.value;
      } finally {
        switchingContext.value = false;
      }
    };
    const onLogout = async () => {
      try {
        await authService.logout();
      } catch {
        // Clear the local session even if the remote logout is unavailable.
      }
      userStore.logout();
      authStore.logout();
      await router.push("/login");
    };
    const closeAccountMenu = () => { accountMenuOpen.value = false; };
    const toggleAccountMenu = () => { accountMenuOpen.value = !accountMenuOpen.value; };
    const onDocumentPointerDown = (event) => {
      if (!accountMenuRoot.value?.contains(event.target)) closeAccountMenu();
    };
    const onDocumentKeydown = (event) => {
      if (event.key === "Escape") closeAccountMenu();
    };
    watch(() => props.open, async (open) => {
      if (!open) return;
      await nextTick();
      closeButton.value?.focus();
    });
    watch(avatarSrc, () => { avatarFailed.value = false; });
    watch(() => router.currentRoute.value.fullPath, closeAccountMenu);
    onMounted(() => {
      document.addEventListener("pointerdown", onDocumentPointerDown);
      document.addEventListener("keydown", onDocumentKeydown);
    });
    onBeforeUnmount(() => {
      document.removeEventListener("pointerdown", onDocumentPointerDown);
      document.removeEventListener("keydown", onDocumentKeydown);
    });
    return {
      accountMenuOpen,
      accountMenuRoot,
      activeContext,
      canReadPlatformUsers,
      canReadPlatformEmails,
      canReadPlatformDomains,
      canReadMembers,
      canReadAudit,
      contextError,
      contextOptions,
      showPlatformNavigation,
      switchingContext,
      switchContext,
      closeButton,
      avatarFailed,
      avatarSrc,
      accountEmail,
      displayName,
      hasTeamContext,
      closeAccountMenu,
      onLogout,
      toggleAccountMenu,
    };
  },
};
</script>
<style>
.sidebar-menu-icon {
  width: 1.05rem;
  height: 1.05rem;
  flex: 0 0 1.05rem;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.sidebar .link:hover .sidebar-menu-icon,
.sidebar .router-link-active .sidebar-menu-icon,
.sidebar .router-link-exact-active .sidebar-menu-icon {
  color: #4cc9f0;
}
.context-switcher {
  margin: 1rem .75rem 1.25rem;
}
.context-switcher label {
  display: block;
  margin-bottom: .35rem;
  color: rgba(255, 255, 255, .65);
  font-size: .7rem;
  font-weight: 700;
  letter-spacing: .08em;
  text-transform: uppercase;
}
.context-switcher select {
  width: 100%;
  padding: .55rem .65rem;
  border: 1px solid rgba(255, 255, 255, .15);
  border-radius: .5rem;
  background: #111d33;
  color: #fff;
  font-size: .78rem;
}
.context-switcher small {
  display: block;
  margin-top: .35rem;
  color: #ff8b94;
}
.dest{
  font-weight: 700;
  display: inline-block;
  max-width: 100%;
  background: linear-gradient(135deg, hsl(291, 80%, 48%), hsl(328, 85%, 60%));
  background-clip: text;
  color: transparent;
  white-space: normal;
  word-break: break-word;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}
</style>
